/**
 * Sprint 60 — Payment Calendar + Status Tracking
 *
 * Components:
 * - PaymentCalendar: main container
 * - SummaryBar: 3 summary cards (total, received, next payment)
 * - TimelineView: horizontal months with stacked payment blocks
 * - ApplicationTracker: status pipeline per tracked program
 * - StatusUpdateModal: inline status update
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';

// ── Status pipeline config ────────────────────────────────────────────────────

const PIPELINE_STEPS = ['PLANNED', 'APPLIED', 'APPROVED', 'ADVANCE_PAID', 'PAID'];

const STATUS_COLOR = {
  PLANNED:      'text-[var(--aif-text-muted)]',
  APPLIED:      'text-blue-400',
  UNDER_REVIEW: 'text-blue-400',
  APPROVED:     'text-yellow-400',
  ADVANCE_PAID: 'text-orange-400',
  PAID:         'text-green-400',
  REJECTED:     'text-red-400',
  NOT_APPLIED:  'text-[var(--aif-text-muted)]',
  PREPARING:    'text-[var(--aif-text-muted)]',
  SUBMITTED:    'text-blue-400',
};

const CONFIDENCE_COLOR = {
  high:   'text-green-400 bg-green-900/20 border-green-700/40',
  medium: 'text-yellow-400 bg-yellow-900/20 border-yellow-700/40',
  low:    'text-[var(--aif-text-muted)] bg-[var(--aif-bg-elevated)] border-[var(--aif-border-default)]',
};

const AMOUNT_COLOR = {
  blue:    'bg-blue-900/40 border-blue-700/50 text-blue-300',
  cyan:    'bg-cyan-900/40 border-cyan-700/50 text-cyan-300',
  teal:    'bg-teal-900/40 border-teal-700/50 text-teal-300',
  green:   'bg-green-900/40 border-green-700/50 text-green-300',
  emerald: 'bg-emerald-900/40 border-emerald-700/50 text-emerald-300',
  purple:  'bg-purple-900/40 border-purple-700/50 text-purple-300',
  gray:    'bg-gray-800/40 border-gray-600/50 text-[var(--aif-text-tertiary)]',
};

function fmt(amount) {
  return amount?.toLocaleString('de-DE') ?? '—';
}

function fmtDate(isoStr) {
  if (!isoStr) return '—';
  return new Date(isoStr + 'T00:00:00').toLocaleDateString('de-DE', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

// ── SummaryBar ────────────────────────────────────────────────────────────────

function SummaryBar({ summary }) {
  const { t } = useTranslation();
  if (!summary) return null;

  return (
    <div className="grid grid-cols-1 gap-2">
      {/* Total expected */}
      <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] px-4 py-3 flex items-center justify-between">
        <div>
          <div className="text-xs text-[var(--aif-text-muted)]">{t('payment.expectedTotal', { year: summary.year })}</div>
          <div className="text-xl font-bold text-[var(--aif-text-primary)]">{fmt(summary.total_expected)} €</div>
        </div>
        {summary.total_received > 0 && (
          <div className="text-right">
            <div className="text-xs text-[var(--aif-text-muted)]">{t('payment.received')}</div>
            <div className="text-lg font-semibold text-green-400">{fmt(summary.total_received)} €</div>
          </div>
        )}
      </div>

      {/* Advance possible */}
      {summary.advance_possible > 0 && (
        <div className="rounded-xl border border-orange-700/40 bg-orange-900/10 px-4 py-2.5 flex items-center gap-3" data-testid="advance-possible-card">
          <span className="text-lg">🗓️</span>
          <div className="flex-1">
            <div className="text-xs text-[var(--aif-text-muted)]">{t('payment.advancePossible')}</div>
            <div className="text-base font-semibold text-orange-400">{fmt(summary.advance_possible)} €</div>
          </div>
          <div className="text-xs text-[var(--aif-text-muted)]">70%</div>
        </div>
      )}

      {/* Next payment */}
      {summary.next_payment && (
        <div className="rounded-xl border border-[var(--aif-color-primary)]/30 bg-[var(--aif-color-primary)]/10 px-4 py-2.5" data-testid="next-payment-card">
          <div className="text-xs text-[var(--aif-text-muted)] mb-0.5">{t('payment.nextPayment')}</div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-[var(--aif-text-primary)] truncate mr-2">
              {summary.next_payment.program}
            </span>
            <div className="text-right shrink-0">
              <div className="text-sm font-bold text-[var(--aif-color-primary)]">{fmt(summary.next_payment.amount)} €</div>
              <div className="text-[10px] text-[var(--aif-text-muted)]">{fmtDate(summary.next_payment.date)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── TimelineView ─────────────────────────────────────────────────────────────

function TimelineView({ timeline }) {
  const { t } = useTranslation();
  if (!timeline?.length) return null;

  const maxTotal = Math.max(...timeline.map(m => m.total), 1);

  return (
    <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3" data-testid="payment-timeline">
      <div className="text-xs font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide mb-3">
        {t('payment.timeline')}
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {timeline.map(month => (
          <div key={month.month} className="shrink-0 min-w-[80px] max-w-[100px]" data-testid={`timeline-month-${month.month}`}>
            {/* Bar */}
            <div className="flex flex-col-reverse gap-0.5 mb-1.5" style={{ height: '80px' }}>
              {month.payments.map((p, i) => {
                const heightPct = Math.max(8, (p.amount / maxTotal) * 100);
                const colors = AMOUNT_COLOR[p.color] || AMOUNT_COLOR.gray;
                return (
                  <div
                    key={i}
                    className={`rounded border ${colors} flex items-center justify-center text-[9px] font-medium transition-all hover:opacity-90`}
                    style={{ height: `${heightPct}%` }}
                    title={`${p.program}: ${fmt(p.amount)} €`}
                  >
                    {p.amount >= 5000 ? `${Math.round(p.amount / 1000)}k` : ''}
                  </div>
                );
              })}
            </div>
            {/* Month label */}
            <div className="text-[10px] text-[var(--aif-text-muted)] text-center leading-tight">
              {month.label.split(' ')[0]}
            </div>
            <div className="text-[10px] font-semibold text-[var(--aif-text-secondary)] text-center">
              {fmt(month.total)} €
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── StatusPipeline ────────────────────────────────────────────────────────────

function StatusPipeline({ currentStatus }) {
  const { t } = useTranslation();
  const isRejected = currentStatus === 'REJECTED';
  if (isRejected) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-red-400">
        <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
        {t('payment.status.REJECTED')}
      </div>
    );
  }

  const currentIdx = PIPELINE_STEPS.indexOf(currentStatus);
  const mappedIdx = {
    SUBMITTED: 1, PREPARING: 0, NOT_APPLIED: 0, APPLIED: 1, UNDER_REVIEW: 2,
  }[currentStatus] ?? currentIdx;

  return (
    <div className="flex items-center gap-1">
      {PIPELINE_STEPS.map((step, i) => {
        const done = i < (mappedIdx >= 0 ? mappedIdx : 0);
        const active = i === (mappedIdx >= 0 ? mappedIdx : 0);
        return (
          <div key={step} className="flex items-center">
            <div
              className={`w-2 h-2 rounded-full shrink-0 ${
                done   ? 'bg-green-500' :
                active ? 'bg-[var(--aif-color-primary)] ring-2 ring-[var(--aif-color-primary)]/30' :
                         'bg-[var(--aif-border-default)]'
              }`}
              title={t(`payment.status.${step}`, step)}
            />
            {i < PIPELINE_STEPS.length - 1 && (
              <div className={`w-4 h-px mx-0.5 ${done ? 'bg-green-500/60' : 'bg-[var(--aif-border-default)]'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── PaymentCard ───────────────────────────────────────────────────────────────

function PaymentCard({ payment, token, onUpdated }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [editingStatus, setEditingStatus] = useState(false);
  const [editingAmount, setEditingAmount] = useState(false);
  const [newStatus, setNewStatus] = useState(payment.status);
  const [newAmount, setNewAmount] = useState(payment.amount_eur || '');
  const [saving, setSaving] = useState(false);

  const isEstimate = payment.program_id?.startsWith('estimate_');
  const colors = AMOUNT_COLOR[payment.color] || AMOUNT_COLOR.gray;
  const confColors = CONFIDENCE_COLOR[payment.confidence] || CONFIDENCE_COLOR.low;

  async function saveStatus() {
    if (isEstimate || !token) return;
    setSaving(true);
    try {
      await apiClient.patch(`/api/v1/aif/subsidies/applications/${encodeURIComponent(payment.program_id)}`, {
        status: newStatus,
        approved_amount: newAmount ? parseFloat(newAmount) : undefined,
      }, { queueOnOffline: true }); // TODO(5b): confirm 3WM — no PATCH/DELETE ETag concurrency yet
      onUpdated?.();
    } finally {
      setSaving(false);
      setEditingStatus(false);
      setEditingAmount(false);
    }
  }

  return (
    <div
      className={`rounded-xl border ${colors} overflow-hidden`}
      data-testid={`payment-card-${payment.program_id}`}
    >
      {/* Header */}
      <button
        className="w-full flex items-start gap-3 px-4 py-3 text-left"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-sm font-medium text-[var(--aif-text-primary)] truncate">
              {payment.program_name}
            </span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${confColors} shrink-0`}>
              {t(`payment.confidence.${payment.confidence}`, payment.confidence)}
            </span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-base font-bold text-[var(--aif-text-primary)]">
              {fmt(payment.amount_eur)} €
            </span>
            <span className="text-xs text-[var(--aif-text-muted)]">
              {payment.expected_payment_period}
            </span>
          </div>
          {!isEstimate && (
            <div className="mt-1.5">
              <StatusPipeline currentStatus={payment.status} />
            </div>
          )}
        </div>
        <span className="text-[var(--aif-text-muted)] text-xs shrink-0 mt-1">
          {expanded ? '▲' : '▼'}
        </span>
      </button>

      {/* Expanded */}
      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-[var(--aif-border-default)] space-y-3">
          {/* Expected date */}
          <div className="flex items-center justify-between text-xs">
            <span className="text-[var(--aif-text-muted)]">{t('payment.expectedDate')}</span>
            <span className="text-[var(--aif-text-secondary)] font-medium">{fmtDate(payment.expected_payment_date)}</span>
          </div>

          {/* Advance payment */}
          {payment.advance_payment && (
            <div className="rounded-xl border border-orange-700/40 bg-orange-900/10 px-3 py-2">
              <div className="text-xs font-semibold text-orange-400 mb-1">{t('payment.advance')}</div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[var(--aif-text-muted)]">{fmt(payment.advance_payment.amount_eur)} €</span>
                <span className="text-[var(--aif-text-muted)]">{fmtDate(payment.advance_payment.expected_date)}</span>
              </div>
              {payment.advance_payment.note && (
                <p className="text-[10px] text-orange-300/80 mt-1">{payment.advance_payment.note}</p>
              )}
            </div>
          )}

          {/* Note */}
          {payment.note && (
            <p className="text-xs text-[var(--aif-text-muted)] italic">ℹ️ {payment.note}</p>
          )}

          {/* Status update (tracked apps only) */}
          {!isEstimate && token && (
            <div className="space-y-2">
              {editingStatus ? (
                <div className="flex gap-2">
                  <select
                    value={newStatus}
                    onChange={e => setNewStatus(e.target.value)}
                    className="flex-1 text-xs rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-input)] px-2 py-1.5 text-[var(--aif-text-primary)]"
                    data-testid={`status-select-${payment.program_id}`}
                  >
                    {['PLANNED','APPLIED','UNDER_REVIEW','APPROVED','ADVANCE_PAID','PAID','REJECTED'].map(s => (
                      <option key={s} value={s}>{t(`payment.status.${s}`, s)}</option>
                    ))}
                  </select>
                  <button
                    onClick={saveStatus}
                    disabled={saving}
                    className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-[var(--aif-color-primary)] text-white"
                    data-testid={`save-status-${payment.program_id}`}
                  >
                    {saving ? '…' : '✓'}
                  </button>
                  <button onClick={() => setEditingStatus(false)} className="px-2 py-1.5 rounded-xl text-xs text-[var(--aif-text-muted)] border border-[var(--aif-border-default)]">✕</button>
                </div>
              ) : (
                <button
                  onClick={() => setEditingStatus(true)}
                  className="w-full py-2 rounded-xl text-xs text-[var(--aif-text-muted)] bg-[var(--aif-bg-input)] border border-[var(--aif-border-default)] hover:border-[var(--aif-color-primary)] hover:text-[var(--aif-color-primary)] transition-all"
                  data-testid={`update-status-btn-${payment.program_id}`}
                >
                  {t('payment.updateStatus')} →
                </button>
              )}

              {/* Amount editing (when APPROVED) */}
              {(payment.status === 'APPROVED' || newStatus === 'APPROVED') && (
                <div className="flex gap-2 items-center">
                  <span className="text-xs text-[var(--aif-text-muted)] shrink-0">{t('payment.approvedAmount')}:</span>
                  <input
                    type="number"
                    value={newAmount}
                    onChange={e => setNewAmount(e.target.value)}
                    placeholder="EUR"
                    className="flex-1 text-xs rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-input)] px-2 py-1.5 text-[var(--aif-text-primary)]"
                    data-testid={`amount-input-${payment.program_id}`}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── PaymentCalendar (main export) ─────────────────────────────────────────────

export default function PaymentCalendar({ token }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch('/api/v1/aif/subsidies/payment-calendar', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="calendar-loading">
        <div className="animate-spin w-6 h-6 rounded-full border-2 border-[var(--aif-color-primary)] border-t-transparent" />
      </div>
    );
  }

  if (!data?.payments?.length) {
    return (
      <div className="text-sm text-[var(--aif-text-muted)] text-center py-8" data-testid="calendar-empty">
        {t('payment.noPayments')}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="payment-calendar">
      {/* Summary */}
      <SummaryBar summary={data.summary} />

      {/* Timeline */}
      {data.timeline?.length > 0 && <TimelineView timeline={data.timeline} />}

      {/* Payment cards */}
      <div className="space-y-2" data-testid="payment-list">
        {data.payments.map(p => (
          <PaymentCard
            key={p.program_id}
            payment={p}
            token={token}
            onUpdated={load}
          />
        ))}
      </div>
    </div>
  );
}
